/**
 * Doors and boss seals (DungeonDef.doors), driven by the sim's door_open / door_close events.
 *
 *  - DoorAnimator (pure): per-door target state + eased progress (0 open .. 1 closed). Closing
 *    takes DOOR_CLOSE_S, opening DOOR_OPEN_S (both ≤ 0.4 s so a seal reads instantly).
 *    encounter_end also opens that encounter's seals (belt and braces; the sim sends door_open).
 *  - buildDoorVisuals (three.js): one small group per door, by style:
 *      wood       iron-bound double gate, bi-fold leaves folded flat along the corridor cheeks
 *      portcullis spiked wooden grille under a stone lintel, hangs raised with its teeth showing
 *      stone      two serpent-face slabs that grind up out of the floor
 *      vines      thorny jade vine curtain that grows up from the floor
 *    Seals get a soft additive glow (under the bloom threshold) while closed.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { DoorDef, DoorId, DoorStyle, SimEvent } from '@keystone/sim';
import { type HullRegistry, addHull, toonColor } from './style';

export const DOOR_CLOSE_S = 0.3;
export const DOOR_OPEN_S = 0.4;

export interface DoorAnimState {
  closed: boolean;
  /** 0 = fully open, 1 = fully closed. */
  p: number;
}

export class DoorAnimator {
  readonly states = new Map<DoorId, DoorAnimState>();
  private readonly seals = new Map<string, DoorId[]>();

  constructor(readonly defs: readonly DoorDef[]) {
    for (const d of defs) {
      if (!d.sealsEncounter) continue;
      this.seals.set(d.sealsEncounter, [...(this.seals.get(d.sealsEncounter) ?? []), d.id]);
    }
    this.reset();
  }

  /** Back to each door's initial state, snapped (new run / restart). */
  reset(): void {
    for (const d of this.defs) {
      const closed = d.initially === 'closed';
      this.states.set(d.id, { closed, p: closed ? 1 : 0 });
    }
  }

  /** Apply sim events. Returns true when any door target changed. */
  ingest(events: readonly SimEvent[] | undefined): boolean {
    if (!events || this.defs.length === 0) return false;
    let changed = false;
    for (const e of events) {
      if (e.type === 'door_close' || e.type === 'door_open') changed = this.set(e.doorId, e.type === 'door_close') || changed;
      else if (e.type === 'encounter_end') for (const id of this.seals.get(e.encounterId) ?? []) changed = this.set(id, false) || changed;
    }
    return changed;
  }

  set(id: DoorId, closed: boolean, instant = false): boolean {
    const s = this.states.get(id);
    if (!s || (s.closed === closed && !instant)) return false;
    s.closed = closed;
    if (instant) s.p = closed ? 1 : 0;
    return true;
  }

  /** Advance animations. Returns true while anything is moving. */
  update(dtS: number): boolean {
    let moving = false;
    for (const s of this.states.values()) {
      const target = s.closed ? 1 : 0;
      if (s.p === target) continue;
      const rate = 1 / (s.closed ? DOOR_CLOSE_S : DOOR_OPEN_S);
      s.p = s.closed ? Math.min(1, s.p + rate * dtS) : Math.max(0, s.p - rate * dtS);
      moving = true;
    }
    return moving;
  }

  progress(id: DoorId): number {
    return this.states.get(id)?.p ?? 0;
  }

  isMoving(id: DoorId): boolean {
    const s = this.states.get(id);
    return !!s && s.p !== (s.closed ? 1 : 0);
  }
}

/** Eased visual closure for a style: heavy things drop / slam (ease-in), vines grow (ease-out). */
export function doorEase(style: DoorStyle, p: number, closing: boolean): number {
  const t = Math.min(1, Math.max(0, p));
  if (style === 'vines') return 1 - (1 - t) * (1 - t);
  if (style === 'portcullis' || style === 'stone') return closing ? t * t : 1 - (1 - t) * (1 - t);
  return t * t * (3 - 2 * t);
}

// ---------------------------------------------------------------------------------------
// Meshes
// ---------------------------------------------------------------------------------------

export interface DoorMaterials {
  wood: THREE.Material;
  stone: THREE.Material;
  face: THREE.Material | null;
  iron: THREE.Material;
  vines: THREE.Material;
}

export interface DoorVisual {
  def: DoorDef;
  group: THREE.Group;
  /** Moving parts, posed by `pose(p)`. */
  pose(p: number, closing: boolean): void;
  glow: THREE.Mesh | null;
}

export interface DoorBuildContext {
  /** Floor height at a sim point. */
  groundAt(x: number, y: number): number;
  /** Whether a sim point is walkable (open wood leaves must fold away from the floor). */
  walkable(x: number, y: number): boolean;
  mats: DoorMaterials;
  hulls: HullRegistry;
  owned: { dispose(): void }[];
}

const GLOW_VERT = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
const GLOW_FRAG = `uniform vec3 uColor; uniform float uK; uniform float uTime; varying vec2 vUv;
  void main(){
    float edge = 1.0 - abs(vUv.x - 0.5) * 2.0;
    float f = pow(clamp(1.0 - vUv.y, 0.0, 1.0), 1.6) * smoothstep(0.0, 0.25, max(edge, 0.0)) * (0.85 + 0.15 * sin(uTime * 3.0 + vUv.x * 9.0));
    gl_FragColor = vec4(uColor * f * uK, f * uK);
  }`;

function sealGlow(width: number, color: [number, number, number], owned: { dispose(): void }[], time: { value: number }): THREE.Mesh {
  const geo = new THREE.PlaneGeometry(width, 3.2).translate(0, 1.6, 0);
  const mat = new THREE.ShaderMaterial({
    uniforms: { uColor: { value: new THREE.Vector3(...color) }, uK: { value: 0 }, uTime: time },
    vertexShader: GLOW_VERT,
    fragmentShader: GLOW_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
  owned.push(geo, mat);
  const m = new THREE.Mesh(geo, mat);
  m.renderOrder = 4;
  m.visible = false;
  return m;
}

/** Box with world-scaled UVs on its faces (planks / sandstone tile at `tile` yards). */
function texBox(w: number, h: number, d: number, tile: number): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  const uv = g.attributes['uv'] as THREE.BufferAttribute;
  const nrm = g.attributes['normal'] as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) {
    const ax = Math.abs(nrm.getX(i));
    const ay = Math.abs(nrm.getY(i));
    const su = ax > 0.5 ? d : w;
    const sv = ay > 0.5 ? d : h;
    uv.setXY(i, (uv.getX(i) * su) / tile, (uv.getY(i) * sv) / tile);
  }
  return g;
}

export function buildDoorVisuals(defs: readonly DoorDef[], ctx: DoorBuildContext, time: { value: number }): DoorVisual[] {
  const out: DoorVisual[] = [];
  for (const def of defs) {
    const group = new THREE.Group();
    group.name = `door:${def.id}`;
    const fx = Math.cos(def.facing);
    const fy = Math.sin(def.facing);
    // Base: lowest floor under the footprint (doors on stairs sink into the steps).
    const depth = def.depth ?? 2;
    let base = Infinity;
    for (const ax of [-0.45, 0, 0.45])
      for (const dz of [-depth / 2, 0, depth / 2]) {
        const sx = def.position.x + fx * dz - fy * ax * (def.width - 1);
        const sy = def.position.y + fy * dz + fx * ax * (def.width - 1);
        base = Math.min(base, ctx.groundAt(sx, sy));
      }
    group.position.set(def.position.x, base, -def.position.y);
    // Local +z = facing (sim direction), local +x = left of it.
    group.rotation.y = Math.atan2(fx, -fy);
    const W = def.width;
    const add = (geo: THREE.BufferGeometry, mat: THREE.Material, parent: THREE.Object3D = group, hull = true): THREE.Mesh => {
      ctx.owned.push(geo);
      const m = new THREE.Mesh(geo, mat);
      m.castShadow = true;
      m.receiveShadow = true;
      m.userData['noStyle'] = true;
      parent.add(m);
      if (hull) addHull(m, mat as THREE.MeshToonMaterial, ctx.hulls, 'prop');
      return m;
    };
    // Frame posts (all styles), plus a lintel for the heavy doors.
    const postH = def.style === 'portcullis' ? 7.0 : def.style === 'stone' ? 5.6 : 4.4;
    const posts = mergeGeometries([texBox(1.3, postH + 0.6, 1.3, 4).translate(W / 2 + 0.5, postH / 2 - 0.3, 0), texBox(1.3, postH + 0.6, 1.3, 4).translate(-W / 2 - 0.5, postH / 2 - 0.3, 0), ...(def.style === 'portcullis' || def.style === 'stone' ? [texBox(W + 2.4, 0.8, 1.4, 4).translate(0, postH + 0.1, 0)] : [])])!;
    add(posts, ctx.mats.stone, group, false);

    let pose: DoorVisual['pose'] = () => {};
    let glow: THREE.Mesh | null = null;
    switch (def.style) {
      case 'wood': {
        // Bi-fold leaves: hinge at the posts; panel 1 swings 90°, panel 2 folds back flat.
        const L = W / 2 - 0.05;
        const half = L / 2;
        const swing = chooseSwing(def, ctx);
        const leaves: { hinge: THREE.Group; fold: THREE.Group; side: number }[] = [];
        for (const side of [-1, 1]) {
          const hinge = new THREE.Group();
          hinge.position.set(side * (W / 2), 0, 0);
          group.add(hinge);
          const p1 = texBox(half, 3.4, 0.28, 3).translate((-side * half) / 2, 1.7, 0);
          add(p1, ctx.mats.wood, hinge);
          // Panel 2 pivots just outside panel 1 so the folded pair stacks instead of overlapping.
          const fold = new THREE.Group();
          fold.position.set(-side * half, 0, 0.16 * swing);
          hinge.add(fold);
          const p2 = texBox(half, 3.4, 0.28, 3).translate((-side * half) / 2, 1.7, -0.16 * swing);
          add(p2, ctx.mats.wood, fold);
          const straps = mergeGeometries([new THREE.BoxGeometry(half * 2, 0.18, 0.34).translate(-side * half, 0.8, 0), new THREE.BoxGeometry(half * 2, 0.18, 0.34).translate(-side * half, 2.6, 0)])!;
          add(straps, ctx.mats.iron, hinge, false);
          leaves.push({ hinge, fold, side });
        }
        pose = (p) => {
          const open = 1 - p;
          for (const lf of leaves) {
            // Open: panel 1 perpendicular to the door line (along the corridor, toward `swing`).
            lf.hinge.rotation.y = lf.side * swing * (Math.PI / 2) * open;
            lf.fold.rotation.y = lf.side * swing * Math.PI * open;
          }
        };
        break;
      }
      case 'portcullis': {
        const h = 4.1;
        const bars: THREE.BufferGeometry[] = [];
        const n = Math.max(4, Math.round(W / 0.75));
        for (let i = 0; i <= n; i++) {
          const x = -W / 2 + 0.2 + ((W - 0.4) * i) / n;
          bars.push(texBox(0.26, h, 0.26, 3).translate(x, h / 2 + 0.25, 0));
          bars.push(new THREE.ConeGeometry(0.17, 0.5, 5).rotateX(Math.PI).translate(x, 0.25, 0).toNonIndexed());
        }
        for (const y of [1.2, 2.6, 3.9]) bars.push(texBox(W, 0.28, 0.34, 3).translate(0, y, 0));
        const geos = bars.map((g) => (g.index ? g.toNonIndexed() : g));
        bars.forEach((g, i) => g !== geos[i] && g.dispose());
        geos.forEach((g) => {
          if (!g.attributes['uv']) g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array((g.attributes['position'] as THREE.BufferAttribute).count * 2), 2));
        });
        const grid = mergeGeometries(geos)!;
        geos.forEach((g) => g.dispose());
        const mover = new THREE.Group();
        group.add(mover);
        add(grid, ctx.mats.wood, mover);
        const raised = postH - h - 0.6;
        pose = (p, closing) => {
          mover.position.y = raised * (1 - doorEase('portcullis', p, closing));
        };
        glow = sealGlow(W, [1.0, 0.55, 0.22], ctx.owned, time);
        break;
      }
      case 'stone': {
        const h = 4.6;
        const slabs = new THREE.Group();
        group.add(slabs);
        for (const side of [-1, 1]) {
          const g = texBox(W / 2 - 0.05, h, 0.9, 4).translate((side * (W / 2 - 0.05)) / 2, h / 2, 0);
          add(g, ctx.mats.stone, slabs);
          if (ctx.mats.face) {
            // The serpent face spans both slabs (each carries its half), on both sides.
            for (const zf of [0.46, -0.46]) {
              const fg = new THREE.PlaneGeometry(W / 2 - 0.05, h - 0.3).translate((side * (W / 2 - 0.05)) / 2, h / 2, zf);
              if (zf < 0) fg.rotateY(Math.PI).translate(0, 0, 0);
              const uv = fg.attributes['uv'] as THREE.BufferAttribute;
              const u0 = side < 0 ? 0 : 0.5;
              for (let i = 0; i < uv.count; i++) uv.setX(i, zf < 0 ? 1 - (u0 + uv.getX(i) * 0.5) : u0 + uv.getX(i) * 0.5);
              add(fg, ctx.mats.face, slabs, false);
            }
          }
        }
        const sunk = -(h + 0.3);
        pose = (p, closing) => {
          slabs.position.y = sunk * (1 - doorEase('stone', p, closing));
          slabs.visible = p > 0.001;
        };
        glow = sealGlow(W, [1.0, 0.62, 0.3], ctx.owned, time);
        break;
      }
      case 'vines': {
        const strands: THREE.BufferGeometry[] = [];
        const n = Math.max(6, Math.round(W / 0.55));
        let seed = 7;
        const rnd = (): number => (seed = (seed * 16807) % 2147483647) / 2147483647;
        for (let i = 0; i < n; i++) {
          const x = -W / 2 + 0.25 + ((W - 0.5) * i) / (n - 1);
          const pts: THREE.Vector3[] = [];
          for (let k = 0; k <= 5; k++) pts.push(new THREE.Vector3(x + Math.sin(k * 1.3 + i) * 0.25, k * 0.82, Math.cos(k * 1.7 + i * 0.5) * 0.25));
          const tube = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 10, 0.09 + rnd() * 0.05, 5, false);
          strands.push(colorize(tube, [0.12, 0.34, 0.2], [0.2, 0.62, 0.42]));
          for (let t = 0; t < 4; t++) {
            const y = 0.5 + rnd() * 3.4;
            const thorn = new THREE.ConeGeometry(0.07, 0.35, 4).rotateZ(rnd() < 0.5 ? 1.4 : -1.4).translate(x + (rnd() - 0.5) * 0.3, y, (rnd() - 0.5) * 0.3);
            strands.push(colorize(thorn, [0.55, 0.62, 0.42], [0.62, 0.7, 0.48]));
            const leaf = new THREE.SphereGeometry(0.22, 5, 3).scale(1, 0.25, 0.55).rotateZ(rnd() * 2).translate(x + (rnd() - 0.5) * 0.4, y + 0.2, (rnd() - 0.5) * 0.4);
            strands.push(colorize(leaf, [0.16, 0.45, 0.3], [0.24, 0.6, 0.4]));
          }
        }
        const geo = mergeGeometries(strands)!;
        strands.forEach((g) => g.dispose());
        const mover = new THREE.Group();
        group.add(mover);
        add(geo, ctx.mats.vines, mover);
        pose = (p, closing) => {
          const k = doorEase('vines', p, closing);
          mover.scale.set(1, Math.max(0.001, k), 1);
          mover.visible = k > 0.002;
        };
        glow = sealGlow(W, [0.25, 0.8, 0.58], ctx.owned, time);
        break;
      }
    }
    if (glow) {
      // The glow is a seal's "you're locked in" cue; plain doors don't get one.
      if (def.sealsEncounter) group.add(glow);
      else glow = null;
    }
    out.push({ def, group, pose, glow });
  }
  return out;
}

function colorize(geo: THREE.BufferGeometry, lo: [number, number, number], hi: [number, number, number]): THREE.BufferGeometry {
  const g = geo.index ? geo.toNonIndexed() : geo;
  if (g !== geo) geo.dispose();
  g.deleteAttribute('uv');
  const pos = g.attributes['position'] as THREE.BufferAttribute;
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = Math.min(1, Math.max(0, pos.getY(i) / 4));
    col[i * 3] = lo[0] + (hi[0] - lo[0]) * t;
    col[i * 3 + 1] = lo[1] + (hi[1] - lo[1]) * t;
    col[i * 3 + 2] = lo[2] + (hi[2] - lo[2]) * t;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

/**
 * Which way wood leaves fold when open: toward +z (the `to` room) or -z, whichever keeps the
 * folded panels off the walkable floor (sampled). Returns +1 / -1.
 */
function chooseSwing(def: DoorDef, ctx: DoorBuildContext): number {
  const fx = Math.cos(def.facing);
  const fy = Math.sin(def.facing);
  const half = def.width / 2;
  const score = (dir: number): number => {
    let bad = 0;
    for (const side of [-1, 1])
      for (let s = 0.3; s <= half / 2 + 0.01; s += 0.4) {
        // Hinge at the strip edge; the folded panel lies along the corridor at that side.
        const lx = side * half;
        const x = def.position.x + fx * dir * s - fy * lx;
        const y = def.position.y + fy * dir * s + fx * lx;
        if (ctx.walkable(x, y)) bad++;
      }
    return bad;
  };
  return score(1) <= score(-1) ? 1 : -1;
}

/** Iron material shared by all door straps. */
export const doorIron = (): THREE.MeshToonMaterial => toonColor(0x3a3a40);
